import { AppSelect } from '../ui/AppSelect';
import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, Send, MessageSquare, Sparkles, Loader2, Trash2, BrainCircuit, FileText, ImagePlus, ExternalLink } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import ActionRenderer, { parseAction, stripAction } from "./ActionRenderer";
import { runTaskToCompletion } from "../../services/taskService";
import { getStoredImageQualityTier } from "../../constants/imageQualityTiers";
import { normalizeWanxImageQualityTier } from "../../utils/wanxImageCapabilities";

import { useDelayedUnmount } from "../../hooks/useDelayedUnmount";

const IMAGE_MESSAGE_PREFIX = "[[AGENT_IMAGE]]";

const usesAgentHistory = (targetMode) => targetMode === "agent" || targetMode === "image";

const parseHistoryMessage = (message) => {
  const content = message?.content || "";
  if (message?.role === "assistant" && content.startsWith(IMAGE_MESSAGE_PREFIX)) {
    try {
      const data = JSON.parse(content.slice(IMAGE_MESSAGE_PREFIX.length));
      if (data?.imageUrl) {
        return { role: "assistant", type: "image", content: "", ...data };
      }
    } catch (error) {
      console.warn("解析文字助手图片消息失败", error);
    }
  }
  return { role: message?.role, content };
};

// 简易 Markdown 渲染
function renderMarkdown(text) {
  if (!text) return "";
  return text
    .replace(/```(\w*)\n?([\s\S]*?)```/g, (_, _lang, code) =>
      `<pre style="background:var(--color-bg-overlay);border-radius:6px;padding:12px;overflow-x:auto;font-size:13px;margin:8px 0;border:1px solid var(--color-border-primary)"><code>${code.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</code></pre>`
    )
    .replace(/`([^`]+)`/g, '<code style="background:var(--color-bg-overlay);padding:1px 5px;border-radius:3px;font-size:13px">$1</code>')
    .replace(/^### (.+)$/gm, '<h4 style="font-weight:700;margin:10px 0 4px">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 style="font-weight:700;margin:12px 0 4px">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 style="font-weight:700;margin:14px 0 6px">$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/^[-*] (.+)$/gm, '<li style="margin-left:16px;list-style:disc">$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li style="margin-left:16px;list-style:decimal">$1</li>')
    .replace(/^={3,}$/gm, '<hr style="border:none;border-top:1px solid var(--color-border-primary);margin:12px 0"/>')
    .replace(/\n/g, "<br/>");
}

const getEpisodeLabel = (episode, index) => {
  if (!episode) return `第${index + 1}集`;
  return episode.title || episode.name || `第${episode.sortOrder || episode.episodeNumber || index + 1}集`;
};

const AiChatPanel = ({ isOpen, onClose, projectId, episodeId, episodes = [], aspectRatio = "16:9" }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  const [messages, setMessages] = useState([]);
  const overlay = useDelayedUnmount(isOpen, 150);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("agent"); // 'agent' | 'image' | 'chat' | 'seedance'
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [scriptEnabled, setScriptEnabled] = useState(true);
  const [scriptScope, setScriptScope] = useState(() => episodeId ? "current" : "all");
  const [selectedScriptEpisodeId, setSelectedScriptEpisodeId] = useState(() => episodeId ? String(episodeId) : "");
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const scriptProjectRef = useRef(projectId);

  useEffect(() => {
    if (scriptProjectRef.current === projectId) return;
    scriptProjectRef.current = projectId;
    setScriptEnabled(true);
    setScriptScope(episodeId ? "current" : "all");
    setSelectedScriptEpisodeId(episodeId ? String(episodeId) : "");
  }, [projectId, episodeId]);

  useEffect(() => {
    const availableEpisodes = Array.isArray(episodes) ? episodes : [];
    setSelectedScriptEpisodeId((current) => {
      if (current && availableEpisodes.some((episode) => String(episode.id) === String(current))) {
        return current;
      }
      if (episodeId && availableEpisodes.some((episode) => String(episode.id) === String(episodeId))) {
        return String(episodeId);
      }
      return availableEpisodes[0]?.id != null ? String(availableEpisodes[0].id) : "";
    });
  }, [episodes, episodeId]);

  useEffect(() => {
    if (scriptScope === "current" && !episodeId) {
      setScriptScope("all");
      return;
    }
    if (scriptScope === "selected" && (!Array.isArray(episodes) || episodes.length === 0)) {
      setScriptScope("all");
    }
  }, [episodeId, episodes, scriptScope]);

  // 加载对话历史
  const loadHistory = useCallback(async (targetMode) => {
    setHistoryLoading(true);
    try {
      let data;
      if (usesAgentHistory(targetMode)) {
        const params = projectId ? `?projectId=${projectId}` : "";
        data = await authenticatedFetch(`${API_HOST}/api/agent/history${params}`);
      } else {
        data = await authenticatedFetch(`${API_HOST}/api/ai-chat/history?mode=${targetMode}`);
      }
      if (Array.isArray(data)) {
        setMessages(data.filter((m) => m.role !== "pipeline_state").map(parseHistoryMessage));
      }
    } catch (e) {
      console.warn("加载对话历史失败", e);
    } finally {
      setHistoryLoading(false);
    }
  }, [projectId]);

  // 面板打开时加载历史
  useEffect(() => {
    if (isOpen) {
      loadHistory(mode);
      setTimeout(() => textareaRef.current?.focus(), 300);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // projectId 变化时，agent 模式重新加载
  useEffect(() => {
    if (isOpen && usesAgentHistory(mode)) {
      loadHistory(mode);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleModeSwitch = useCallback((newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    setStreamingContent("");
    loadHistory(newMode);
  }, [mode, loadHistory]);

  const handleClearHistory = useCallback(async () => {
    try {
      if (usesAgentHistory(mode)) {
        const params = projectId ? `?projectId=${projectId}` : "";
        await authenticatedFetch(`${API_HOST}/api/agent/history${params}`, { method: "DELETE" });
      } else {
        await authenticatedFetch(`${API_HOST}/api/ai-chat/history?mode=${mode}`, { method: "DELETE" });
      }
      setMessages([]);
      setStreamingContent("");
    } catch (e) {
      console.warn("清空历史失败", e);
    }
  }, [mode, projectId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, streamingContent]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape" && isOpen) onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  // Agent 发送：提交到后端总闸任务队列，轮询完成后写入对话
  const handleAgentSend = useCallback(async (text) => {
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);
    setStreamingContent("");

    try {
      const provider = localStorage.getItem("ai_provider") || "omini";
      const scopedEpisodeId = scriptScope === "current"
        ? episodeId
        : scriptScope === "selected"
          ? selectedScriptEpisodeId
          : null;
      const scriptEpisodeIds = scopedEpisodeId != null && scopedEpisodeId !== ""
        ? [Number(scopedEpisodeId)]
        : [];
      const task = await runTaskToCompletion("AGENT_CHAT", {
        projectId: projectId ? Number(projectId) : null,
        message: text,
        scriptEnabled,
        scriptScope,
        scriptEpisodeIds: scriptEpisodeIds.filter(Number.isFinite),
      }, {
        provider,
        projectId: projectId ? Number(projectId) : null,
        source: "agent_chat",
        timeoutMs: 60 * 60 * 1000,
      });
      const result = typeof task.result === "string"
        ? task.result
        : (task.result?.value || task.result?.result || task.result?.reply || "");
      setMessages((prev) => [...prev, { role: "assistant", content: result || "（无返回内容）" }]);
      setStreamingContent("");
    } catch (err) {
      setMessages((prev) => [...prev, { role: "assistant", content: `出错了: ${err.message}` }]);
      setStreamingContent("");
    } finally {
      setLoading(false);
    }
  }, [episodeId, projectId, scriptEnabled, scriptScope, selectedScriptEpisodeId]);

  // 对话生图：后端读取并校验剧本范围，再复用统一生图总闸。
  const handleImageSend = useCallback(async (text) => {
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);
    setStreamingContent("");

    try {
      const provider = localStorage.getItem("ai_provider") || "omini";
      const scopedEpisodeId = scriptScope === "current"
        ? episodeId
        : scriptScope === "selected"
          ? selectedScriptEpisodeId
          : null;
      const scriptEpisodeIds = scopedEpisodeId != null && scopedEpisodeId !== ""
        ? [Number(scopedEpisodeId)].filter(Number.isFinite)
        : [];
      const imageQualityTier = normalizeWanxImageQualityTier(
        getStoredImageQualityTier(),
        provider,
      );
      const task = await runTaskToCompletion("AGENT_IMAGE_GENERATE", {
        projectId: projectId ? Number(projectId) : null,
        episodeId: episodeId ? Number(episodeId) : null,
        message: text,
        scriptEnabled,
        scriptScope,
        scriptEpisodeIds,
        aspectRatio,
        imageQualityTier,
      }, {
        provider,
        projectId: projectId ? Number(projectId) : null,
        episodeId: episodeId ? Number(episodeId) : null,
        source: "agent_chat_image",
        timeoutMs: 60 * 60 * 1000,
      });
      const result = task.result && typeof task.result === "object" ? task.result : {};
      const imageUrl = task.url || result.imageUrl || result.url;
      if (!imageUrl) throw new Error("生图完成，但没有返回图片地址");
      setMessages((prev) => [...prev, {
        role: "assistant",
        type: "image",
        content: "",
        imageUrl,
        prompt: text,
        provider: result.provider || provider,
        aspectRatio: result.aspectRatio || aspectRatio,
        imageQualityTier: result.imageQualityTier || imageQualityTier,
      }]);
    } catch (err) {
      setMessages((prev) => [...prev, { role: "assistant", content: `生图失败: ${err.message}` }]);
    } finally {
      setLoading(false);
    }
  }, [aspectRatio, episodeId, projectId, scriptEnabled, scriptScope, selectedScriptEpisodeId]);

  // 普通模式发送（chat / seedance）
  const handleLegacySend = useCallback(async (text) => {
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);
    try {
      let result;
      if (mode === "seedance") {
        const task = await runTaskToCompletion("SEEDANCE_FROM_SCRIPT", {
          script: text,
          projectId: projectId || localStorage.getItem("currentProjectId") || null,
        }, {
          projectId: projectId || localStorage.getItem("currentProjectId") || null,
          source: "ai_chat_seedance",
        });
        result = typeof task.result === "string" ? task.result : (task.result?.value || task.result?.result || "");
      } else {
        const task = await runTaskToCompletion("AI_CHAT", {
          prompt: text,
          projectId: projectId || localStorage.getItem("currentProjectId") || null,
        }, {
          projectId: projectId || localStorage.getItem("currentProjectId") || null,
          source: "ai_chat",
        });
        result = typeof task.result === "string" ? task.result : (task.result?.value || task.result?.result || "");
      }
      setMessages((prev) => [...prev, { role: "assistant", content: result || "（无返回内容）" }]);
    } catch (err) {
      setMessages((prev) => [...prev, { role: "assistant", content: `出错了: ${err.message}` }]);
    } finally {
      setLoading(false);
    }
  }, [mode, projectId]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    if (mode === "agent") {
      handleAgentSend(text);
    } else if (mode === "image") {
      handleImageSend(text);
    } else {
      handleLegacySend(text);
    }
  }, [input, loading, mode, handleAgentSend, handleImageSend, handleLegacySend]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const MODES = [
    { id: "agent", label: "创作助手", icon: BrainCircuit, color: theme.colors.accentPrimary },
    { id: "image", label: "生图", icon: ImagePlus, color: theme.colors.greenPrimary },
    { id: "chat", label: "自由对话", icon: MessageSquare, color: theme.colors.bluePrimary },
    { id: "seedance", label: "视频提示词", icon: Sparkles, color: theme.colors.purplePrimary },
  ];
  const activeMode = MODES.find((m) => m.id === mode);
  const availableEpisodes = Array.isArray(episodes) ? episodes : [];
  const currentScriptEpisode = availableEpisodes.find((episode) => String(episode.id) === String(episodeId));
  const scriptScopeOptions = [
    { id: "all", label: "全部剧本", disabled: !projectId },
    { id: "current", label: "本集剧本", disabled: !episodeId },
    { id: "selected", label: "自选集数", disabled: availableEpisodes.length === 0 },
  ];

  return (
    <>
      {overlay.mounted && (
        <div
          className={`fixed inset-0 z-40 ${overlay.exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-200"}`}
          style={{ backgroundColor: "var(--overlay-light)" }}
          onClick={onClose}
        />
      )}

      <div
        className="fixed top-0 right-0 h-full z-50 flex flex-col transition-transform duration-300 ease-in-out shadow-2xl"
        style={{
          width: "50vw",
          minWidth: 560,
          maxWidth: "90vw",
          transform: isOpen ? "translateX(0)" : "translateX(100%)",
          backgroundColor: "var(--color-bg-secondary)",
          borderLeft: "1px solid var(--color-border-primary)",
        }}
      >
        {/* 头部 */}
        <div className="flex items-center justify-between px-4 py-3 shrink-0"
          style={{ borderBottom: "1px solid var(--color-border-primary)" }}>
          <div className="flex items-center gap-2">
            {activeMode && <activeMode.icon size={18} style={{ color: activeMode.color }} />}
            <span className="font-bold text-sm" style={{ color: "var(--color-text-primary)" }}>
              {activeMode?.label || "文字助手"}
            </span>
            {usesAgentHistory(mode) && projectId && (
              <span className="text-2xs px-1.5 py-0.5 rounded"
                style={{ backgroundColor: `${theme.colors.accentPrimary}15`, color: theme.colors.accentPrimary }}>
                项目上下文
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button onClick={handleClearHistory}
                className="p-1.5 rounded-md transition-colors hover:opacity-80"
                style={{ color: "var(--color-text-muted)" }}
                title="清空对话" aria-label="清空对话">
                <Trash2 size={15} />
              </button>
            )}
            <button onClick={onClose}
              className="p-1.5 rounded-md transition-colors hover:opacity-80"
              style={{ color: "var(--color-text-muted)" }} aria-label="关闭">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* 消息列表 */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 custom-scrollbar" style={{ minHeight: 0 }}>
          {historyLoading && (
            <div className="flex items-center justify-center py-8 gap-2">
              <Loader2 size={16} className="animate-spin" style={{ color: "var(--color-text-muted)" }} />
              <span className="text-sm" style={{ color: "var(--color-text-muted)" }}>加载历史...</span>
            </div>
          )}

          {!historyLoading && messages.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center h-full gap-3 opacity-50">
              {activeMode && <activeMode.icon size={36} style={{ color: "var(--color-text-muted)" }} />}
              <p className="text-sm text-center" style={{ color: "var(--color-text-muted)" }}>
                {mode === "agent"
                  ? projectId ? "我是你的创作助手「小悦」，对你的项目了如指掌。\n问我任何关于剧本、角色、分镜的问题吧" : "选择一个项目后，我就能理解你的创作上下文"
                  : mode === "image" ? "描述想要的画面；开启剧本后，会结合选定范围理解人物、场景和情绪"
                  : mode === "seedance" ? "输入剧本或故事，生成视频提示词"
                  : "开始对话吧"}
              </p>
            </div>
          )}

          {messages.map((msg, i) => {
            const isImageMessage = msg.role === "assistant" && msg.type === "image" && msg.imageUrl;
            const action = msg.role === "assistant" && !isImageMessage ? parseAction(msg.content) : null;
            const displayText = action ? stripAction(msg.content) : msg.content;

            return (
            <div key={i} className={`flex animate-in fade-in slide-in-from-bottom-1 duration-200 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className="max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed"
                style={
                  msg.role === "user"
                    ? {
                        background: "var(--color-accent-primary)",
                        color: "white",
                        borderBottomRightRadius: 4,
                      }
                    : {
                        backgroundColor: "var(--color-bg-tertiary)",
                        color: "var(--color-text-primary)",
                        border: "1px solid var(--color-border-primary)",
                        borderBottomLeftRadius: 4,
                      }
                }
              >
                {isImageMessage ? (
                  <div className="min-w-[260px] max-w-[520px]">
                    <a href={msg.imageUrl} target="_blank" rel="noreferrer" className="group block overflow-hidden rounded-lg">
                      <img
                        src={msg.imageUrl}
                        alt={msg.prompt || "文字助手生成图片"}
                        loading="lazy"
                        className="block h-auto w-full object-contain transition-transform duration-300 group-hover:scale-[1.01]"
                        style={{ backgroundColor: "var(--color-bg-overlay)" }}
                      />
                    </a>
                    <div className="mt-2 flex min-w-0 items-center justify-between gap-2 text-2xs" style={{ color: "var(--color-text-muted)" }}>
                      <span className="truncate">
                        {[msg.provider, msg.imageQualityTier, msg.aspectRatio].filter(Boolean).join(" · ") || "图片已生成"}
                      </span>
                      <a href={msg.imageUrl} target="_blank" rel="noreferrer" className="shrink-0 p-1" title="查看原图" aria-label="查看原图">
                        <ExternalLink size={13} />
                      </a>
                    </div>
                  </div>
                ) : msg.role === "assistant" ? (
                  <>
                    <div className="ai-chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(displayText) }} />
                    {action && (
                      <ActionRenderer
                        action={action}
                        projectId={projectId}
                        onSendMessage={(text) => {
                          setInput("");
                          if (mode === "agent") handleAgentSend(text);
                          else if (mode === "image") handleImageSend(text);
                          else handleLegacySend(text);
                        }}
                      />
                    )}
                  </>
                ) : (
                  <span style={{ whiteSpace: "pre-wrap" }}>{msg.content}</span>
                )}
              </div>
            </div>
            );
          })}

          {/* 流式输出中 — 隐藏 ACTION 标记 */}
          {streamingContent && (
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed"
                style={{
                  backgroundColor: "var(--color-bg-tertiary)",
                  color: "var(--color-text-primary)",
                  border: "1px solid var(--color-border-primary)",
                  borderBottomLeftRadius: 4,
                }}>
                <div className="ai-chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(stripAction(streamingContent)) }} />
                <span className="inline-block w-1.5 h-4 ml-0.5 animate-pulse rounded-sm"
                  style={{ backgroundColor: theme.colors.accentPrimary, verticalAlign: "text-bottom" }} />
              </div>
            </div>
          )}

          {/* 非流式 loading */}
          {loading && !streamingContent && (
            <div className="flex justify-start">
              <div className="rounded-xl px-4 py-3 flex items-center gap-2"
                style={{ backgroundColor: "var(--color-bg-tertiary)", border: "1px solid var(--color-border-primary)" }}>
                <Loader2 size={14} className="animate-spin" style={{ color: theme.colors.accentPrimary }} />
                <span className="text-sm" style={{ color: "var(--color-text-muted)" }}>
                  {mode === "image" ? "生图中..." : "思考中..."}
                </span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* 模式切换 + 输入区 */}
        <div className="shrink-0 px-4 pb-4 pt-2" style={{ borderTop: "1px solid var(--color-border-primary)" }}>
          <div className="flex items-center gap-1.5 mb-2">
            {MODES.map((m) => (
              <button key={m.id}
                onClick={() => handleModeSwitch(m.id)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-2xs font-medium transition-all"
                style={{
                  backgroundColor: mode === m.id ? `${m.color}15` : "transparent",
                  color: mode === m.id ? m.color : "var(--color-text-muted)",
                  border: `1px solid ${mode === m.id ? `${m.color}40` : "var(--color-border-primary)"}`,
                }}>
                <m.icon size={12} />
                {m.label}
              </button>
            ))}
            {/* 创作助手与生图共用同一套剧本范围。 */}
            {usesAgentHistory(mode) && (
              <label
                className="ml-auto flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1.5 text-2xs font-medium transition-all"
                style={{
                  backgroundColor: scriptEnabled ? `${theme.colors.greenPrimary}15` : "transparent",
                  color: scriptEnabled ? theme.colors.greenPrimary : "var(--color-text-muted)",
                  border: `1px solid ${scriptEnabled ? `${theme.colors.greenPrimary}40` : "var(--color-border-primary)"}`,
                }}
                title={scriptEnabled ? "发送时携带选定范围内的剧本" : "点击启用剧本上下文"}>
                <input
                  type="checkbox"
                  checked={scriptEnabled}
                  onChange={(event) => setScriptEnabled(event.target.checked)}
                  disabled={!projectId}
                  className="h-3.5 w-3.5 cursor-pointer"
                  style={{ accentColor: theme.colors.greenPrimary }}
                />
                <FileText size={11} />
                剧本
              </label>
            )}
          </div>

          {usesAgentHistory(mode) && scriptEnabled && projectId && (
            <div
              className="mb-2 flex min-w-0 flex-wrap items-center gap-2 rounded-lg border p-1.5"
              style={{
                backgroundColor: "var(--color-bg-tertiary)",
                borderColor: `${theme.colors.greenPrimary}30`,
              }}
            >
              <div className="flex shrink-0 items-center gap-1 rounded-md p-0.5" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
                {scriptScopeOptions.map((option) => {
                  const selected = scriptScope === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      disabled={option.disabled}
                      aria-pressed={selected}
                      onClick={() => setScriptScope(option.id)}
                      className="h-7 rounded-md px-2 text-2xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-35"
                      style={{
                        color: selected ? theme.colors.greenPrimary : "var(--color-text-muted)",
                        backgroundColor: selected ? `${theme.colors.greenPrimary}18` : "transparent",
                        border: `1px solid ${selected ? `${theme.colors.greenPrimary}45` : "transparent"}`,
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>

              {scriptScope === "current" && (
                <span
                  className="min-w-0 flex-1 truncate px-1 text-2xs"
                  style={{ color: "var(--color-text-secondary)" }}
                  title={currentScriptEpisode ? getEpisodeLabel(currentScriptEpisode, 0) : "当前页面没有集数"}
                >
                  {currentScriptEpisode ? getEpisodeLabel(currentScriptEpisode, 0) : "当前页面没有集数"}
                </span>
              )}

              {scriptScope === "selected" && (
                <AppSelect
                  value={selectedScriptEpisodeId}
                  onChange={(event) => setSelectedScriptEpisodeId(event.target.value)}
                  className="h-8 min-w-[180px] flex-1 rounded-md border px-2 text-xs outline-none"
                  style={{
                    color: "var(--color-text-primary)",
                    backgroundColor: "var(--color-bg-primary)",
                    borderColor: "var(--color-border-primary)",
                  }}
                  aria-label="选择要加入文字助手上下文的剧本集数"
                >
                  {availableEpisodes.map((episode, index) => (
                    <option key={episode.id} value={episode.id}>
                      {getEpisodeLabel(episode, index)}
                    </option>
                  ))}
                </AppSelect>
              )}
            </div>
          )}

          <div className="flex items-end gap-2 rounded-xl p-3"
            style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)" }}>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                mode === "agent" ? "问我关于项目的任何问题..."
                : mode === "image" ? "描述要生成的画面..."
                : mode === "seedance" ? "输入剧本或故事内容..."
                : "输入消息..."
              }
              rows={3}
              className="flex-1 resize-none bg-transparent outline-none text-sm leading-relaxed"
              style={{ color: "var(--color-text-primary)", maxHeight: 200, minHeight: 60 }}
              onInput={(e) => {
                e.target.style.height = "auto";
                e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px";
              }}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              className="p-2 rounded-lg transition-all shrink-0"
              style={{
                backgroundColor: input.trim() && !loading ? theme.colors.accentPrimary : "var(--color-bg-tertiary)",
                color: input.trim() && !loading ? "white" : "var(--color-text-muted)",
                opacity: input.trim() && !loading ? 1 : 0.5,
              }}
              aria-label={mode === "image" ? "生图" : "发送"}
              title={mode === "image" ? "生图" : "发送"}>
              {mode === "image" ? <ImagePlus size={16} /> : <Send size={16} />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AiChatPanel;
